// src/components/ui/InputPanel.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Send, ChevronDown, Mic, MicOff } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import demoCommands from '../../data/demo_commands.json';

interface InputPanelProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onDemoSelect?: (input: string, output: string) => void;
  onClearExample?: () => void;
  isLoading: boolean;
  disabled?: boolean;
  maxLength?: number;
  isDemoSelected?: boolean;
}

export const InputPanel: React.FC<InputPanelProps> = ({
  value,
  onChange,
  onSubmit,
  onDemoSelect,
  onClearExample,
  isLoading,
  disabled = false,
  maxLength = 250,
  isDemoSelected = false
}) => {
  const [showDemoDropdown, setShowDemoDropdown] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event: any) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        onChange(transcript);
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      recognitionInstance.onerror = () => {
        setIsListening(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, [onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDemoDropdown(false);
      }
    };

    if (showDemoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDemoDropdown]);
  const handleVoiceInput = () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  const handleSubmit = () => {
    if (!value.trim() || isLoading || disabled) return;
    onSubmit();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const handleDemoSelect = (demo: { command: string; purpose: string }) => {
    onChange(demo.purpose);
    if (onDemoSelect) {
      onDemoSelect(demo.purpose, demo.command);
    }
    setShowDemoDropdown(false);
  };

  const isOverLimit = value.length > maxLength;

  return (
    <div className="flex-1 bg-white border-r lg:border-r border-b lg:border-b-0 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-900">Input</h2>
        <div className="flex items-center gap-2">
          {isOverLimit && (
            <span className="text-sm text-red-600 font-medium">
              {value.length} / {maxLength} characters
            </span>
          )}
          <button
            onClick={handleVoiceInput}
            disabled={disabled}
            className={`p-1 rounded transition-colors ${
              isListening 
                ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            } disabled:opacity-50`}
            title={isListening ? 'Stop recording' : 'Start voice input'}
          >
            {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col p-2 lg:p-4 max-h-80 lg:max-h-80">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Enter your text here... (e.g., 'List all cows in the hospital pen')"
          className={`w-full h-32 lg:h-40 p-2 lg:p-4 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base ${
            isOverLimit ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
          disabled={disabled}
          maxLength={maxLength + 50}
        />
        
        <div className="mt-2 lg:mt-4 flex flex-row justify-between items-center gap-2">
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={() => setShowDemoDropdown(!showDemoDropdown)}
              disabled={disabled}
              className="flex items-center gap-2 px-3 py-2 text-xs lg:text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
            >
              Demo Examples
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showDemoDropdown && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                <div className="p-2 border-b bg-gray-50">
                  <p className="text-xs text-gray-600 font-medium">Select a demo command:</p>
                </div>
                {demoCommands.demo_commands.map((demo, index) => (
                  <button
                    key={index}
                    onClick={() => handleDemoSelect(demo)}
                    className="w-full text-left p-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {demo.purpose}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-row gap-2 flex-shrink-0">
            {onClearExample && isDemoSelected && (
              <button
                onClick={onClearExample}
                disabled={disabled}
                className="flex items-center gap-1 text-xs lg:text-sm text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                <span>✕</span>
                <span>Clear Example</span>
              </button>
            )}

            <button
              onClick={handleSubmit}
              disabled={!value.trim() || isLoading || disabled || isOverLimit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs lg:text-sm whitespace-nowrap"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isLoading ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};